import { apiFiles } from "@/api/file.api.ts";
import { DocumentFolderEditModal } from "@/components/documents/widgets/DocumentFolderEditModal.tsx";
import { DocumentEsignProgressBar } from "@/components/documents/widgets/DocumentEsignProgressBar.tsx";
import { DocumentEsignStatus } from "@/components/documents/widgets/DocumentEsignStatus.tsx";
import { EmployeeDocumentEsignModal } from "@/pages/esign/widgets/EmployeeDocumentEsignModal.tsx";
import { EsignEnvelopesModal } from "@/pages/esign/widgets/EsignEnvelopesModal.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeDocumentPreviewModal } from "@/pages/employees/employee/widgets/EmployeeDocumentPreviewModal.tsx";
import { EsignRequestModal } from "@/pages/esign/widgets/EsignRequestModal.tsx";
import {
  useDocument,
  useDocumentDelete,
  useDocumentsFolderDelete,
  useDocumentsSearch,
} from "@/queries/use-documents.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiDocument,
  ApiDocumentsFilter,
  ApiEsignMarkupStatus,
  ApiFolder,
  ApiFolderType,
  folderParamKeys,
} from "@/types/document.types.ts";
import { ESIGN_MARKUP_REFERRER_KEY } from "@/pages/esign/esign.config.ts";
import { EsignSignatureType } from "@/types/esign.types.ts";
import { ApiEmployee, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  Card,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  EmptyStateBase,
  FontAwesomeIcon,
  LinkButtonTo,
  PaginationBlock,
  SelectItem,
  Skeleton,
  Stack,
  Table,
  Tag,
  Text,
  Title,
  useDebouncedSearchParam,
  useModals,
} from "@skailer-com/uikit";
import { CellContext, ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { DocumentDropOverlay } from "./DocumentDropOverlay.tsx";
import { BulkUploadStatus, useDocumentBulkUpload } from "./use-document-bulk-upload.ts";
import { DocumentEditModal } from "./widgets/DocumentEditModal.tsx";
import { DocumentMoveFileModal } from "./widgets/DocumentMoveFileModal.tsx";

interface UploadingRow {
  __uploading: true;
  id: string;
  name: string;
  status: BulkUploadStatus;
}
type DocumentTableRow = ApiDocument | UploadingRow;
const isUploadingRow = (row: DocumentTableRow): row is UploadingRow => "__uploading" in row;

export interface DocumentsProps {
  folders: ApiFolder[];
  isLoading: boolean;
  foldersType: ApiFolderType;
  employee?: ApiEmployee;
  showManageFolders?: boolean;
  enableSearch?: boolean;
  hideListHeader?: boolean;
}

export const Documents = ({
  folders,
  isLoading,
  foldersType,
  employee,
  showManageFolders = false,
  enableSearch = false,
  hideListHeader = false,
}: DocumentsProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderDate, renderEmployeeName } = useFormatter();
  const { downloadFile } = useDownloadHelper();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const canManageEsignTemplates =
    isFeatureEnabled(ApiFeatureType.esign) && canIEdit(ApiPermission.esign_manage_templates);
  const canRequestEsign =
    isFeatureEnabled(ApiFeatureType.esign) && canIEdit(ApiPermission.esign_request);
  const canViewEsignReport =
    isFeatureEnabled(ApiFeatureType.esign) && canIEdit(ApiPermission.esign_download_report);
  const canSeeEmployeeEsignStatus = canRequestEsign || canViewEsignReport;
  const [downloadingDocIds, setDownloadingDocIds] = useState<Set<number>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const canCreateFolder = canIEdit(ApiPermission.settings_document_folders);

  const esignRecipientOption = useMemo<SelectItem | undefined>(
    () =>
      foldersType === ApiFolderType.employee && employee
        ? {
            id: employee.id,
            title: renderEmployeeName(employee),
            avatarUrl: getEmployeeAvatarUrl(employee, "sm"),
            caption: getEmployeePositionTitle(employee),
          }
        : undefined,
    [foldersType, employee, renderEmployeeName]
  );

  const [isDragOver, setIsDragOver] = useState(false);

  const isFolderClickDisabled = foldersType === ApiFolderType.employee && !employee;

  const folderParamKey = folderParamKeys[foldersType];
  const folderIdParam = searchParams.get(folderParamKey);
  const selectedFolder = useMemo(() => {
    const folderId = folderIdParam ? parseInt(folderIdParam, 10) : undefined;
    if (!folderId) return undefined;
    if (isFolderClickDisabled) return undefined;
    return folders?.find((folder) => folder.id === folderId);
  }, [folderIdParam, isFolderClickDisabled, folders]);

  const canDrop = !!selectedFolder?.acl.can_edit_documents;

  const { queue, addFiles } = useDocumentBulkUpload({
    folderId: selectedFolder?.id ?? 0,
    employeeId: employee?.id ?? null,
  });

  const [documentsFilter, setDocumentsFilter] = useState<Omit<ApiDocumentsFilter, "folder_id">>({
    page_number: 1,
    employee_id: employee?.id,
  });

  const [syncedFolderId, setSyncedFolderId] = useState(selectedFolder?.id);

  if (selectedFolder?.id !== syncedFolderId) {
    setSyncedFolderId(selectedFolder?.id);
    if (selectedFolder) setDocumentsFilter((prev) => ({ ...prev, page_number: 1 }));
  }

  const {
    data: documents,
    isFetching,
    isLoading: isDocumentsLoading,
  } = useDocumentsSearch(
    { ...documentsFilter, folder_id: selectedFolder?.id ?? 0 },
    !!selectedFolder
  );

  const { debouncedValue: searchQuery } = useDebouncedSearchParam({
    paramName: "docs_search",
    delay: 300,
  });

  const isSearching = enableSearch && !selectedFolder && searchQuery.trim().length > 0;

  const [searchFilter, setSearchFilter] = useState<Omit<ApiDocumentsFilter, "folder_id">>({
    page_number: 1,
    employee_id: employee?.id,
  });

  const [syncedSearchQuery, setSyncedSearchQuery] = useState(searchQuery);

  if (searchQuery !== syncedSearchQuery) {
    setSyncedSearchQuery(searchQuery);
    setSearchFilter((prev) => ({ ...prev, page_number: 1 }));
  }

  const {
    data: searchResults,
    isFetching: isSearchFetching,
    isLoading: isSearchLoading,
  } = useDocumentsSearch(
    { ...searchFilter, folder_type: foldersType, title: searchQuery },
    isSearching
  );

  const esignRequestParam = searchParams.get("esign_request");
  const esignRequestDocId = esignRequestParam ? parseInt(esignRequestParam, 10) : null;
  const { data: esignRequestDoc } = useDocument(esignRequestDocId ?? 0, esignRequestDocId != null);
  const esignRequestOpenedRef = useRef(false);

  useEffect(() => {
    if (esignRequestDocId == null) {
      esignRequestOpenedRef.current = false;
      return;
    }
    if (esignRequestOpenedRef.current || !esignRequestDoc) return;
    esignRequestOpenedRef.current = true;
    openModal(EsignRequestModal, {
      documentId: esignRequestDoc.id,
      documentTitle: esignRequestDoc.title,
      file: esignRequestDoc.file,
      fileName: esignRequestDoc.file?.name ?? "",
      preview: esignRequestDoc.preview,
      signatureType: EsignSignatureType.signature,
      isEmployeeDocument: esignRequestDoc.folder.type === ApiFolderType.employee,
      sourceFolderId: esignRequestDoc.folder.id,
      sourceFolderTitle: esignRequestDoc.folder.title,
      defaultRecipient: esignRecipientOption,
    });
    setSearchParams(
      (prev) => {
        prev.delete("esign_request");
        return prev;
      },
      { replace: true }
    );
  }, [esignRequestDocId, esignRequestDoc, esignRecipientOption, openModal, setSearchParams]);

  const folderDelete = useDocumentsFolderDelete();
  const documentDelete = useDocumentDelete();

  const handleEditOrCreateFolder = useCallback((folder: ApiFolder | null, type: ApiFolderType) => {
    openModal(DocumentFolderEditModal, { folder, type });
  }, []);

  const handleDeleteFolder = useCallback((folder: ApiFolder) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await folderDelete.mutateAsync({ id: folder.id });
          analyticsService.trackEvent(analyticEvents.documents.folderDeleted, {
            [analyticProperties.id]: folder.id,
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  const handleOpenFile = useCallback(
    (document: ApiDocument) => {
      if (!document.file) return;

      openModal(EmployeeDocumentPreviewModal, {
        file: document.file,
        preview: document.preview,
        documentTitle: document.title,
        documentUpdatedAt: document.datetime_updated_at,
        documentId: document.id,
        analyticsSource: "Employee Documents Table",
      });
    },
    [openModal]
  );

  const handleDownloadFile = useCallback(
    (targetDocument: ApiDocument) => {
      const file = targetDocument.file;
      if (!file) return;

      setDownloadingDocIds((prev) => new Set(prev).add(targetDocument.id));

      downloadFile({
        apiCall: () => apiFiles.getPrivate(file.uuid),
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.documents.downloaded, {
            [analyticProperties.id]: targetDocument.id,
            [analyticProperties.source]: "Employee Documents Table",
            [analyticProperties.title]: targetDocument.title,
          });
        },
        onFinally: () => {
          setDownloadingDocIds((prev) => {
            const next = new Set(prev);
            next.delete(targetDocument.id);
            return next;
          });
        },
      });
    },
    [downloadFile]
  );

  const handleEditOrCreateDocument = useCallback(
    (document: ApiDocument | undefined = undefined) => {
      if (!selectedFolder) return;
      openModal(DocumentEditModal, {
        folderId: selectedFolder.id,
        employeeId: employee?.id ?? null,
        document: document ?? null,
      });
    },
    [selectedFolder, employee]
  );

  const handleMoveDocumentAction = useCallback(
    (document: ApiDocument) => {
      openModal(DocumentMoveFileModal, {
        document: document,
        employeeId: employee?.id ?? null,
      });
    },
    [employee]
  );

  const handleDeleteDocumentAction = useCallback((document: ApiDocument) => {
    openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await documentDelete.mutateAsync(document.id);
          analyticsService.trackEvent(analyticEvents.documents.deleted, {
            [analyticProperties.id]: document.id,
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  const tableData = useMemo<DocumentTableRow[]>(
    () => [
      ...queue
        .filter((item) => item.status !== "done")
        .map((item) => ({
          __uploading: true as const,
          id: item.id,
          name: item.name,
          status: item.status,
        })),
      ...(documents?.items ?? []),
    ],
    [queue, documents?.items]
  );

  const whiteSpaceClasses = "whitespace-nowrap text-ellipsis overflow-hidden block w-full";
  const columns = useMemo<ColumnDef<DocumentTableRow>[]>(() => {
    if (!selectedFolder) return [];

    const inner: ColumnDef<DocumentTableRow>[] = [
      {
        header: t("common:date"),
        cell: (row) => {
          if (isUploadingRow(row.row.original)) return null;
          return <Text>{renderDate(row.row.original.datetime_updated_at)}</Text>;
        },
        accessorKey: "datetime_updated_at",
        ...ColumnSizes.historicalDate,
      },
      {
        header: t("core:document"),
        cell: (row) => {
          const original = row.row.original;
          if (isUploadingRow(original)) {
            const icon =
              original.status === "uploading"
                ? "fa-light fa-spinner"
                : original.status === "error"
                  ? "fa-light fa-xmark"
                  : "fa-light fa-clock";
            return (
              <Stack
                items={"center"}
                direction={"horizontal"}
                gap={"sm"}
                className={"text-secondary"}
              >
                <FontAwesomeIcon
                  icon={icon}
                  className={original.status === "uploading" ? "animate-spin" : ""}
                />
                <Text className={twMerge("text-secondary", whiteSpaceClasses)}>
                  {original.name}
                </Text>
              </Stack>
            );
          }
          return (
            <Stack items={"start"} className={""}>
              {original.file ? (
                <LinkButtonTo
                  variant={"secondary"}
                  onClick={() => {
                    void handleOpenFile(original);
                  }}
                  className={twMerge("text-title", whiteSpaceClasses)}
                >
                  {original.title}
                </LinkButtonTo>
              ) : (
                <Text className={twMerge("text-title", whiteSpaceClasses)}>{original.title}</Text>
              )}
              {foldersType === ApiFolderType.employee &&
                canSeeEmployeeEsignStatus &&
                original.esign_result && (
                  <DocumentEsignStatus
                    esignResult={original.esign_result}
                    onClick={() =>
                      openModal(EmployeeDocumentEsignModal, {
                        esignResult: original.esign_result!,
                        documentId: original.id,
                        documentTitle: original.title,
                      })
                    }
                  />
                )}
            </Stack>
          );
        },
        accessorKey: "title",
        ...ColumnSizes.wide,
      },
      ...(foldersType === ApiFolderType.organization && canManageEsignTemplates
        ? [
            {
              header: t("core:ready_for_signing"),
              cell: (row: CellContext<DocumentTableRow, unknown>) => {
                const original = row.row.original;
                if (isUploadingRow(original)) return null;
                if (original.esign_markup_status !== ApiEsignMarkupStatus.ready) return null;
                return <FontAwesomeIcon icon={"fa-light fa-check"} className={"text-secondary"} />;
              },
              accessorKey: "esign_markup_status",
              ...ColumnSizes.status,
            },
          ]
        : []),
      ...(foldersType === ApiFolderType.organization && canRequestEsign
        ? [
            {
              header: t("common:progress"),
              cell: (row: CellContext<DocumentTableRow, unknown>) => {
                const original = row.row.original;
                if (isUploadingRow(original) || !original.esign_envelopes_progress) return null;

                const {
                  completed_count: completed,
                  active_count: active,
                  total_count: total,
                } = original.esign_envelopes_progress;

                return (
                  <DocumentEsignProgressBar
                    documentId={original.id}
                    documentTitle={original.title}
                    completed={completed}
                    active={active}
                    total={total}
                  />
                );
              },
              accessorKey: "esign_envelopes_progress",
              ...ColumnSizes.middle,
            },
          ]
        : []),
      {
        header: t("common:uploaded_by"),
        cell: (row) => {
          if (isUploadingRow(row.row.original)) return null;
          return (
            <EmployeeHoverCard
              employee={row.row.original.uploaded_by}
              position={getEmployeePositionTitle(row.row.original.uploaded_by)}
            >
              <EmployeeListItem behavior={"card"} employee={row.row.original.uploaded_by} />
            </EmployeeHoverCard>
          );
        },
        accessorKey: "owner",
        ...ColumnSizes.employee,
      },
    ];

    return inner;
  }, [
    selectedFolder,
    foldersType,
    canManageEsignTemplates,
    canRequestEsign,
    canSeeEmployeeEsignStatus,
    openModal,
  ]);

  const renderRowAction = useCallback(
    (row: Row<DocumentTableRow>) => {
      if (isUploadingRow(row.original)) return null;
      const doc = row.original;
      const canEdit = doc.acl.can_edit;
      const canDelete = doc.acl.can_delete;
      const hasFile = !!doc.file;

      if (!hasFile && !canEdit && !canDelete) return null;
      const isOrgFolder = foldersType === ApiFolderType.organization;
      const canMarkup =
        (isOrgFolder ? canManageEsignTemplates : canRequestEsign) && doc.can_acknowledge;
      const isMarkupReady = doc.esign_markup_status === ApiEsignMarkupStatus.ready;
      const showRequestSignatures = canRequestEsign && doc.can_sign;
      const showRequestAcknowledgment = canRequestEsign && doc.can_acknowledge;
      const showPrepareForSigning = canMarkup && !isMarkupReady;
      const showEditSigning = canMarkup && isMarkupReady;
      const showEsignGroup =
        showRequestSignatures ||
        showRequestAcknowledgment ||
        showPrepareForSigning ||
        showEditSigning;

      const openRequest = (signatureType: EsignSignatureType) => {
        openModal(EsignRequestModal, {
          documentId: doc.id,
          documentTitle: doc.title,
          file: doc.file,
          fileName: doc.file?.name ?? "",
          preview: doc.preview,
          signatureType,
          isEmployeeDocument: doc.folder.type === ApiFolderType.employee,
          sourceFolderId: doc.folder.id,
          sourceFolderTitle: doc.folder.title,
          defaultRecipient: esignRecipientOption,
        });
      };

      const storeMarkupReferrer = () => {
        sessionStorage.setItem(
          ESIGN_MARKUP_REFERRER_KEY,
          window.location.pathname + window.location.search
        );
      };

      return (
        <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
          {downloadingDocIds.has(doc.id) && (
            <Stack justify={"center"}>
              <FontAwesomeIcon icon="fa-light fa-spinner" className="animate-spin" />
            </Stack>
          )}
          <DropdownMenu>
            {hasFile && (
              <DropdownMenuItem
                onClick={() => {
                  void handleDownloadFile(doc);
                }}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-download"} />}
                title={t("common:download")}
              />
            )}

            {isOrgFolder && canRequestEsign && doc.esign_envelopes_progress && (
              <DropdownMenuItem
                onClick={() =>
                  openModal(EsignEnvelopesModal, {
                    documentId: doc.id,
                    documentTitle: doc.title,
                    completedEnvelopesCount: doc.esign_envelopes_progress!.completed_count,
                    activeEnvelopesCount: doc.esign_envelopes_progress!.active_count,
                    totalEnvelopesCount: doc.esign_envelopes_progress!.total_count,
                  })
                }
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-bars-progress"} />}
                title={t("core:esign_signing_progress")}
              />
            )}

            {foldersType === ApiFolderType.employee &&
              canSeeEmployeeEsignStatus &&
              doc.esign_result && (
                <DropdownMenuItem
                  onClick={() =>
                    openModal(EmployeeDocumentEsignModal, {
                      esignResult: doc.esign_result!,
                      documentId: doc.id,
                      documentTitle: doc.title,
                    })
                  }
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-file-circle-check"} />}
                  title={t("core:esign_signing_status")}
                />
              )}

            {showEsignGroup && <DropdownMenuSeparator />}
            {showRequestSignatures && (
              <DropdownMenuItem
                onClick={() => {
                  openRequest(EsignSignatureType.signature);
                }}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-signature"} />}
                title={t("core:esign_request_signatures")}
                description={t("core:esign_request_signatures_description")}
              />
            )}
            {showRequestAcknowledgment && (
              <DropdownMenuItem
                onClick={() => {
                  openRequest(EsignSignatureType.acknowledge);
                }}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-square-check"} />}
                title={t("core:esign_request_acknowledgment")}
                description={t("core:esign_request_acknowledgment_description")}
              />
            )}
            {showPrepareForSigning && (
              <DropdownMenuItem
                to={`/docs/documents/${doc.id}/markup`}
                onClick={storeMarkupReferrer}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-file-signature"} />}
                title={t("core:prepare_for_signing")}
                description={t("core:prepare_for_signing_description")}
              />
            )}
            {showEditSigning && (
              <DropdownMenuItem
                to={`/docs/documents/${doc.id}/markup`}
                onClick={storeMarkupReferrer}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-file-signature"} />}
                title={t("core:edit_signing")}
                description={t("core:edit_signing_description")}
              />
            )}

            {(canEdit || canDelete) && (hasFile || showEsignGroup) && <DropdownMenuSeparator />}
            {canEdit && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    handleEditOrCreateDocument(doc);
                  }}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                  title={t("common:edit")}
                />
                <DropdownMenuItem
                  onClick={() => {
                    handleMoveDocumentAction(doc);
                  }}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-folder-gear"} />}
                  title={t("common:move_to") + "..."}
                />
              </>
            )}
            {canDelete && (
              <DropdownMenuItem
                onClick={() => {
                  handleDeleteDocumentAction(doc);
                }}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                variant={"menuDanger"}
                title={t("common:delete") + "..."}
              />
            )}
          </DropdownMenu>
        </Stack>
      );
    },
    [
      foldersType,
      canManageEsignTemplates,
      canRequestEsign,
      canSeeEmployeeEsignStatus,
      esignRecipientOption,
      openModal,
      handleEditOrCreateDocument,
      handleMoveDocumentAction,
      handleDeleteDocumentAction,
      handleDownloadFile,
      downloadingDocIds,
      t,
    ]
  );

  const searchColumns = useMemo<ColumnDef<ApiDocument>[]>(
    () => [
      {
        header: t("common:date"),
        cell: (row) => <Text>{renderDate(row.row.original.datetime_updated_at)}</Text>,
        accessorKey: "datetime_updated_at",
        ...ColumnSizes.historicalDate,
      },
      {
        header: t("core:document"),
        cell: (row) => {
          const doc = row.row.original;
          const file = doc.file;
          if (!file) {
            return <Text className={twMerge("text-title", whiteSpaceClasses)}>{doc.title}</Text>;
          }
          return (
            <LinkButtonTo
              variant={"secondary"}
              onClick={() => {
                openModal(EmployeeDocumentPreviewModal, {
                  file,
                  preview: doc.preview,
                  documentTitle: doc.title,
                  documentUpdatedAt: doc.datetime_updated_at,
                  documentId: doc.id,
                  analyticsSource: "Documents Search",
                });
              }}
              className={twMerge("text-title", whiteSpaceClasses)}
            >
              {doc.title}
            </LinkButtonTo>
          );
        },
        accessorKey: "title",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:folder"),
        cell: (row) => <Text className={whiteSpaceClasses}>{row.row.original.folder.title}</Text>,
        accessorKey: "folder",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:uploaded_by"),
        cell: (row) => (
          <EmployeeHoverCard
            employee={row.row.original.uploaded_by}
            position={getEmployeePositionTitle(row.row.original.uploaded_by)}
          >
            <EmployeeListItem behavior={"card"} employee={row.row.original.uploaded_by} />
          </EmployeeHoverCard>
        ),
        accessorKey: "owner",
        ...ColumnSizes.employee,
      },
    ],
    [t, renderDate, openModal]
  );

  const renderSearchRowAction = useCallback(
    (row: Row<ApiDocument>) => {
      const doc = row.original;
      if (!doc.file) return null;
      return (
        <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
          {downloadingDocIds.has(doc.id) && (
            <Stack justify={"center"}>
              <FontAwesomeIcon icon="fa-light fa-spinner" className="animate-spin" />
            </Stack>
          )}
          <DropdownMenu>
            <DropdownMenuItem
              onClick={() => {
                void handleDownloadFile(doc);
              }}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-download"} />}
              title={t("common:download")}
            />
          </DropdownMenu>
        </Stack>
      );
    },
    [downloadingDocIds, handleDownloadFile, t]
  );

  const title =
    foldersType === ApiFolderType.organization
      ? t("core:organization_documents")
      : t("core:employee_documents");

  return (
    <div
      className="relative"
      onDragEnter={(e) => {
        if (!canDrop || !e.dataTransfer.types.includes("Files")) return;
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setIsDragOver(false);
      }}
      onDragOver={(e) => {
        if (canDrop) e.preventDefault();
      }}
      onDrop={(e) => e.preventDefault()}
    >
      <Stack gap={"lg"}>
        {(selectedFolder || !hideListHeader) && (
          <Stack justify={"between"} direction={"horizontal"} items={"start"}>
            {selectedFolder ? (
              <>
                <Stack gap={"md"}>
                  <ButtonIcon
                    variant={"secondary"}
                    icon={<FontAwesomeIcon icon="fa-light fa-arrow-left" className={"text-lg"} />}
                    onClick={() => {
                      setSearchParams((prev) => {
                        prev.delete(folderParamKey);
                        return prev;
                      });
                    }}
                  />
                  <Title
                    header={selectedFolder.title}
                    caption={
                      isFetching
                        ? `${t("common:loading")}...`
                        : t("plurals:files_uploaded", {
                            count: documents?.metadata.total_items ?? 0,
                          })
                    }
                    size={"lg"}
                  />
                </Stack>
                {selectedFolder.acl.can_edit_documents && (
                  <Button
                    variant={"primary"}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                    size={"md"}
                    onClick={() => {
                      handleEditOrCreateDocument();
                    }}
                  >
                    {t("core:create_document")}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Title header={title} size={"lg"} />
                {showManageFolders && canCreateFolder && (
                  <Button
                    variant={"primary"}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-folder-plus"} />}
                    size={"md"}
                    onClick={() => {
                      handleEditOrCreateFolder(null, foldersType);
                    }}
                  >
                    {t("core:create_folder")}
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}

        {selectedFolder ? (
          <>
            <Table
              renderRowAction={renderRowAction}
              data={tableData}
              columns={columns}
              isFetching={isFetching || documentDelete.isPending}
              showSkeleton={isDocumentsLoading}
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={
                <EmptyStateBase
                  emoji={emojiDefaults.documents.default}
                  title={t("core:no_documents_created")}
                  description={t("core:no_documents_created_description")}
                  primaryButton={
                    selectedFolder.acl.can_edit_documents ? (
                      <Button
                        onClick={() => {
                          handleEditOrCreateDocument();
                        }}
                        size={"sm"}
                        variant={"secondary"}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-upload"} />}
                      >
                        {t("core:create_document")}
                      </Button>
                    ) : undefined
                  }
                />
              }
            />
            <PaginationBlock
              metadata={documents?.metadata}
              onPageChange={(page) => {
                setDocumentsFilter((prev) => ({ ...prev, page_number: page }));
              }}
            />
          </>
        ) : (
          <Box>
            {isSearching ? (
              <>
                <Table
                  renderRowAction={renderSearchRowAction}
                  data={searchResults?.items ?? []}
                  columns={searchColumns}
                  isFetching={isSearchFetching}
                  showSkeleton={isSearchLoading}
                  showNotFoundError={searchResults?.metadata.total_items === 0}
                  notFoundComponent={<EmptyStateNothingFound />}
                />
                <PaginationBlock
                  metadata={searchResults?.metadata}
                  onPageChange={(page) => {
                    setSearchFilter((prev) => ({ ...prev, page_number: page }));
                  }}
                />
              </>
            ) : isLoading ? (
              <Stack
                direction={"horizontal"}
                className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"}
              >
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className={"h-48 w-full"} />
                ))}
              </Stack>
            ) : folders && folders.length > 0 ? (
              <Stack
                direction={"horizontal"}
                gap={"md"}
                className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"}
              >
                {folders.map((folder) => (
                  <Card
                    key={`folder-${folder.id}`}
                    header={folder.title}
                    onClick={
                      isFolderClickDisabled
                        ? undefined
                        : () => {
                            setSearchParams((prev) => {
                              prev.set(folderParamKey, String(folder.id));
                              return prev;
                            });
                          }
                    }
                    buttonsSlot={
                      <Tag>{t("plurals:documents_count", { count: folder.document_count })}</Tag>
                    }
                    menu={
                      showManageFolders &&
                      (folder.acl.can_edit || folder.acl.can_delete) && (
                        <>
                          {folder.acl.can_edit && (
                            <DropdownMenuItem
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.preventDefault();
                                handleEditOrCreateFolder(folder, folder.type);
                              }}
                              leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                              title={t("common:edit")}
                            />
                          )}
                          {folder.acl.can_delete && (
                            <DropdownMenuItem
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.preventDefault();
                                handleDeleteFolder(folder);
                              }}
                              leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                              variant={"menuDanger"}
                              title={t("common:delete")}
                            />
                          )}
                        </>
                      )
                    }
                  />
                ))}
              </Stack>
            ) : (
              <Stack className={"bg-dark/5 rounded-xs"}>
                <EmptyStateBase
                  emoji={emojiDefaults.documentFolders.default}
                  title={t("core:no_folders_created")}
                  description={t(
                    foldersType === ApiFolderType.organization
                      ? "core:no_org_folders_description"
                      : "core:no_employee_folders_description"
                  )}
                  primaryButton={
                    canCreateFolder && (
                      <Button
                        onClick={() => {
                          if (showManageFolders) {
                            handleEditOrCreateFolder(null, foldersType);
                          } else {
                            navigate("/docs/settings");
                          }
                        }}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                      >
                        {t("core:create_folder")}
                      </Button>
                    )
                  }
                />
              </Stack>
            )}
          </Box>
        )}
      </Stack>
      {isDragOver && (
        <DocumentDropOverlay
          onDrop={(files) => {
            addFiles(files);
            setIsDragOver(false);
          }}
        />
      )}
    </div>
  );
};
